/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import React from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import Icon from '../../../components/Icon'

import { Flex } from '../../../components/Layout'
import Picture from '../../../components/Picture'

const POPOUT_WIDTH = 214

// eslint-disable-next-line react/display-name
const GameInfoPopout = React.forwardRef(
  ({ isOpen, slots = [], isInFrame }, ref) => {
    return (
      <div
        ref={ref}
        className='game-info-popout'
        css={css`
          position: absolute;
          right: -${POPOUT_WIDTH + 15}px;
          top: 0;
          width: ${POPOUT_WIDTH}px;
          border-radius: 8px;
          background-color: ${colors.SOFT_STEEL};
          // To appear above the seed numbers
          z-index: 3;

          filter: drop-shadow(-4px 4px 4px rgba(0, 0, 0, 0.15));

          .slot:nth-of-type(even) {
            background-color: rgba(0, 0, 0, 0.15);
          }

          // Removing this due to https://sportninja.atlassian.net/browse/SN-3206
          // transition: all 0.2s ease-in-out;
          pointer-events: ${isOpen ? 'auto' : 'none'};
          opacity: ${isOpen ? 1 : 0};
          transform: translateX(${isOpen ? 0 : -50}px);

          svg {
            position: absolute;
            z-index: 2;
            left: -11px;
            top: 7px;
            transition: 0.2s ease-in-out;
            polygon {
              fill: ${colors.SOFT_STEEL};
              stroke: ${colors.SOFT_STEEL};
              stroke-width: 2;
            }
          }
        `}
      >
        <svg width='22' height='22' viewBox='-2 -2 22 22'>
          <polygon
            className='triangle'
            strokeLinejoin='round'
            points='16,0 16,16 0,8'
          />
        </svg>
        {slots.map((slot) => {
          const {
            id: slotId,
            game_id: SNGameUID,
            ended_at,
            game_number,
            homeTeamSlot,
            visitingTeamSlot,
            home_team_score,
            visiting_team_score,
          } = slot

          const winningTeam =
            home_team_score > visiting_team_score
              ? homeTeamSlot
              : visitingTeamSlot

          return (
            <Flex
              as={SNGameUID ? (isInFrame ? Link : 'a') : undefined}
              href={
                SNGameUID && !isInFrame
                  ? `${ROUTES.GAME_ROOT}/${SNGameUID}`
                  : undefined
              }
              to={
                SNGameUID && isInFrame
                  ? `${ROUTES.GAME_ROOT}/${SNGameUID}`
                  : undefined
              }
              rel={isInFrame ? undefined : 'noreferrer'}
              target={isInFrame ? undefined : '_blank'}
              className='slot'
              key={slotId}
              alignItems='center'
              css={css`
                position: relative;
                min-height: 80px;
                padding: 16px;
                ${SNGameUID &&
                css`
                  cursor: pointer;
                `}

                &:hover .link-icon {
                  color: ${colors.DEFAULT_FLAIR};
                }
              `}
            >
              {(ended_at ||
                homeTeamSlot?.team_id ||
                visitingTeamSlot?.team_id) && (
                <Flex
                  noFlex
                  column
                  alignItems='center'
                  css={css`
                    margin-right: 8px;
                    min-width: 32px;

                    .team-image:nth-of-type(2) {
                      margin-top: 6px;
                    }

                    i {
                      transform: translateX(1px);
                    }
                  `}
                >
                  {ended_at ? (
                    <Picture
                      className='team-image'
                      imageId={getImageThumbnailId(winningTeam)}
                      iconName='user-friends'
                      size='xxsmall'
                      square
                      fit
                    />
                  ) : (
                    <>
                      {homeTeamSlot.team_id && (
                        <Picture
                          className='team-image'
                          imageId={getImageThumbnailId(homeTeamSlot)}
                          iconName='user-friends'
                          size='xxxsmall'
                          square
                          fit
                        />
                      )}
                      {visitingTeamSlot.team_id && (
                        <Picture
                          className='team-image'
                          imageId={getImageThumbnailId(visitingTeamSlot)}
                          iconName='user-friends'
                          size='xxxsmall'
                          square
                          fit
                        />
                      )}
                    </>
                  )}
                </Flex>
              )}

              <div>
                <div
                  css={css`
                    font-size: 16px;
                    font-weight: 700;
                  `}
                >
                  Game {game_number}
                </div>
                {ended_at ? (
                  <>
                    <div
                      css={css`
                        margin-top: 4px;
                        font-size: 12px;
                        font-weight: 500;
                        color: ${colors.ATTENDANCE_GRAY};
                      `}
                    >
                      {home_team_score > visiting_team_score
                        ? `${home_team_score} - ${visiting_team_score}`
                        : `${visiting_team_score} - ${home_team_score}`}
                    </div>
                    <div
                      css={css`
                        margin-top: 4px;
                        font-size: 12px;
                        font-weight: 500;
                        color: ${colors.ATTENDANCE_GRAY};
                      `}
                    >
                      Winner: {winningTeam?.name_full}
                    </div>
                  </>
                ) : (
                  <div
                    css={css`
                      margin-top: 4px;
                      font-size: 12px;
                      font-weight: 500;
                      color: ${colors.ATTENDANCE_GRAY};
                    `}
                  >
                    {homeTeamSlot?.name_full} vs. {visitingTeamSlot?.name_full}
                  </div>
                )}
              </div>
              {SNGameUID && (
                <Icon
                  className='link-icon'
                  name='external-link-alt'
                  faType='far'
                  fontSize={14}
                  color={colors.ATTENDANCE_GRAY}
                  css={css`
                    position: absolute;
                    right: 8px;
                    top: 8px;
                  `}
                />
              )}
            </Flex>
          )
        })}
      </div>
    )
  }
)

const mapStateToProps = (state) => {
  return {
    isInFrame: state.auth.inFrame,
  }
}

export default connect(mapStateToProps)(GameInfoPopout)
